import { gql } from '@/__generated__/gql';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { Loader } from '@/components/loaders/loader';
import { InfoList } from '@/components/table/anchor-drop-down/infoList';
import { InfoListRow } from '@/components/table/anchor-drop-down/infoList/types';
import { useAuth } from '@/hooks/use-auth';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';

export const CLIENT_DETAILS_SUMMARY_QUERY = gql(`
  query FatEntityDetailsPageClientEntityDetailsSummary($id: String!, $includingProposed: Boolean) {
    legalEntity(id: $id, includingProposed: $includingProposed) {
      entityName
      exportId
      importId
      totalEntityValue
      privatePercentage
      targetOCRatio
      currentOCRatio
      ignoreBiteSize
      ignoreAllocationTargets
      alwaysShow
      fundBiteSize
      directBiteSize
      onHold
      custodied
      taxExempt
      impact
      sectorsToAvoid
      progressBarValues {
        economicExposure
        totalCapitalTarget
        capitalTarget
        isGoalAchieved
      }
    }
  }
`);

interface EntityPopoverProps {
  id: string;
  allocation: any;
  type: string;
  alwaysShow: boolean;
  tooltipContent: string;
}

const EntityPopover = ({ id, allocation, type, alwaysShow, tooltipContent }: EntityPopoverProps) => {
  const { user } = useAuth();

  const [switcher, setSwitcher] = useState(true);

  const { data, refetch, loading } = useQuery(CLIENT_DETAILS_SUMMARY_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: allocation.legalEntity.id,
      includeRecommendation: switcher
    }
  });

  const windowHeight = window.innerHeight;
  const isTwoColumn = windowHeight - 215 <= 600;

  useEffect(() => {
    setSwitcher(true);
    refetch();
  }, []);

  const changeSwitcher = () => {
    setSwitcher(!switcher);
    refetch({
      id: allocation.legalEntity.id,
      includeRecommendation: !switcher
    });
  };

  if (data === undefined || loading) {
    return <Loader />;
  }

  const simplifiedEntityPopover: InfoListRow[] = [
    {
      name: 'On Hold',
      formatType: 'yesNo',
      value: data.legalEntity.onHold
    },
    {
      name: 'Always Show',
      formatType: 'yesNo',
      value: user && user.tenant.type === TenantType.ADVISORY_FIRM && data.legalEntity.alwaysShow ? data.legalEntity.alwaysShow : null
    },
    {
      name: 'Tax Exempt',
      formatType: 'yesNo',
      value: data.legalEntity.taxExempt
    },
    {
      name: 'Bite Size',
      tooltipName: type === 'Illiquid Alternative' ? 'Bite Size for Illiquid Alternative' : 'Bite Size for Restricted Alternative',
      formatType: 'JSX',
      value: data.legalEntity.ignoreBiteSize ? (
        <>Use minimum</>
      ) : (
        <FormattingTooltip>{type === 'Illiquid Alternative' ? data.legalEntity.fundBiteSize : data.legalEntity.directBiteSize}</FormattingTooltip>
      )
    },
    {
      name: 'Sectors To Avoid',
      tooltipName: undefined,
      formatType: 'string',
      value: data.legalEntity.sectorsToAvoid
    }
  ];

  const entityPopover: InfoListRow[] = [
    {
      name: 'On Hold',
      formatType: 'yesNo',
      value: data.legalEntity.onHold
    },
    {
      name: 'Always Show',
      formatType: 'yesNo',
      value: data.legalEntity.alwaysShow ? data.legalEntity.alwaysShow : null
    },
    {
      name: 'Tax Exempt',
      formatType: 'yesNo',
      value: data.legalEntity.taxExempt
    },
    {
      name: 'Entity Value',
      formatType: 'JSX',
      value: <FormattingTooltip>{data.legalEntity.totalEntityValue}</FormattingTooltip>
    },
    {
      name: '% To Private Capital',
      formatType: 'percentage',
      value: data.legalEntity.privatePercentage
    },
    {
      name: 'Target OC Ratio',
      formatType: 'x',
      value: data.legalEntity.targetOCRatio
    }
  ];

  return (
    <InfoList
      isTwoColumn={isTwoColumn}
      data={data.legalEntity.ignoreAllocationTargets ? simplifiedEntityPopover : entityPopover}
      isSwitcherActive={switcher}
      setIsSwitcherActive={changeSwitcher}
      label={'Include Pending Commitments'}
    />
  );
};

export default EntityPopover;
